import { Skeleton } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthData } from '../../context/auth';
import { ClaimData, withClaimContext } from '../../context/claim';
import { PolicyData } from '../../context/policy';
import {
	PAYABLE_DETAILS_CANCEL_ACTION,
	PAYABLE_DETAILS_CLOSE_ACTION,
	PAYABLE_DETAILS_HEADER,
	PAYABLE_DETAILS_REVERSE_ACTION,
	PAYABLE_DETAILS_REVERSE_CONFIRMATION_MSG,
	PAYABLE_DETAIL_REVERSED_LINK_TEXT,
} from '../../data/labelsAndMessages';
import { LedgerLineItem, SubClaimPayableResponse } from '../../types/claim';
import { CurrencyCode } from '../../types/currencyCode';
import colors from '../../util/palette';
import { formatCurrency, formatUNIXTimestampWithTime } from '../../util/util';
import DefaultDialog from '../DefaultDialog';
import { errorToast } from '../Toast';
import {
	formatLedgerType,
	formatPayableAmount,
	formatPayableType,
} from './util';

interface Props {
	authData: AuthData;
	claimData: ClaimData;
	policyData: PolicyData;
	locator: string;
	ledger: LedgerLineItem;
	currency: CurrencyCode;
	openOverride?: boolean;
}

const PayableDetailsModal = ({
	authData,
	claimData,
	locator,
	ledger,
	currency,
	openOverride = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(openOverride);
	const [payable, setPayable] = useState<SubClaimPayableResponse>(null);
	const [payableLocator, setPayableLocator] = useState<string>(locator);
	const [loading, setLoading] = useState(true);
	const [confirmReverse, setConfirmReverse] = useState(false);

	useEffect(() => {
		const fetchPayable = async () => {
			try {
				setLoading(true);
				const subClaimPayable = await claimData.fetchSubclaimPayable(
					payableLocator,
				);
				setPayable(subClaimPayable);
				setLoading(false);
			} catch (error) {
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
				errorToast('Failed to fetch payable');
			}
		};
		fetchPayable();
	}, [payableLocator, authData, claimData]);

	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);
	const confirm = () => setConfirmReverse(true);
	const closeConfirm = () => setConfirmReverse(false);

	const reversePayable = async () => {
		try {
			setIsOpen(false);
			setConfirmReverse(false);
			const reversedPayable = await claimData.reverseSubclaimPayable(
				payable.claimLocator,
				{ payableLocator },
			);
			setPayable(reversedPayable);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to reverse payable');
		}
	};

	const togglePayable = (reverseLocator: string) => {
		setPayableLocator(reverseLocator);
	};

	const heading = confirmReverse
		? `Reverse ${PAYABLE_DETAILS_HEADER()} ${payableLocator}`
		: `${PAYABLE_DETAILS_HEADER()} ${payableLocator}`;

	const detailActions =
		!payable?.reversalLocator && !payable?.reversedLocator
			? [
					{
						text: PAYABLE_DETAILS_REVERSE_ACTION(),
						onClick: confirm,
					},
					{ text: PAYABLE_DETAILS_CLOSE_ACTION(), onClick: close },
			  ]
			: [{ text: PAYABLE_DETAILS_CLOSE_ACTION(), onClick: close }];
	const confirmActions = [
		{
			text: PAYABLE_DETAILS_REVERSE_ACTION(),
			onClick: reversePayable,
		},
		{ text: PAYABLE_DETAILS_CANCEL_ACTION(), onClick: closeConfirm },
	];

	const getReversalLink = (isReversal: boolean) => {
		const linkedLocator = payable.reversalLocator || payable.reversedLocator;
		const reverseLinkText = isReversal
			? `reversal of `
			: payable.reversalLocator
			? `reversed by `
			: null;
		const payableLink = (
			<a
				onClick={() => togglePayable(linkedLocator)}
				style={{ cursor: 'pointer', color: colors.primary.main }}
			>
				{PAYABLE_DETAILS_HEADER()} {linkedLocator}
			</a>
		);
		return reverseLinkText ? (
			<span>
				{PAYABLE_DETAIL_REVERSED_LINK_TEXT()} {isReversal ? 'a' : null}{' '}
				<b>{reverseLinkText}</b> {payableLink}{' '}
			</span>
		) : null;
	};

	const loadingContent = () => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<div
					style={{ display: 'flex', justifyContent: 'left', marginTop: '10px' }}
				>
					<Skeleton
						variant='rectangular'
						width={150}
						height={50}
						style={{ marginRight: '20px' }}
					/>
					<Skeleton variant='rectangular' width={150} height={50} />
				</div>
				<div
					style={{ display: 'flex', justifyContent: 'left', marginTop: '10px' }}
				>
					<Skeleton
						variant='rectangular'
						width={150}
						height={50}
						style={{ marginRight: '20px' }}
					/>
					<Skeleton variant='rectangular' width={150} height={50} />
				</div>
				<Skeleton
					variant='rectangular'
					width='100%'
					height={50}
					style={{ marginTop: '10px' }}
				/>
			</div>
		);
	};

	const renderPayableDetails = () => {
		const isReversal = payable.reversedLocator;
		const payableAmount = formatPayableAmount(
			payable.amount,
			currency,
			!!isReversal,
		);
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<span>{getReversalLink(!!isReversal)}</span>
				<div
					style={{ display: 'flex', justifyContent: 'left', marginTop: '10px' }}
				>
					<TextField
						id='type'
						type='string'
						style={{ marginRight: '20px' }}
						label={'Type'}
						value={formatPayableType(payable.reserveType)}
						disabled
					/>
					<TextField
						id='amount'
						type='string'
						label={'Amount'}
						value={payableAmount}
						disabled
					/>
				</div>
				<div
					style={{ display: 'flex', justifyContent: 'left', marginTop: '10px' }}
				>
					<TextField
						id='createdTimestamp'
						type='string'
						style={{ marginRight: '20px' }}
						label={'Created'}
						value={formatUNIXTimestampWithTime(
							payable.createdTimestamp,
							authData.auth.timezone,
						)}
						disabled
					/>
					<TextField
						id='recipient'
						type='string'
						label={'Recipient'}
						value={payable.recipient ? payable.recipient : 'Not Specified'}
						disabled
					/>
				</div>
				<TextField
					id='comment'
					multiline
					maxRows={4}
					style={{ width: '100%', marginRight: '10px', marginTop: '10px' }}
					label={'Comment'}
					value={payable.comment ? payable.comment : 'Not Specified'}
					disabled
				/>
			</div>
		);
	};

	const renderModalContent = () => {
		if (loading) {
			return loadingContent();
		}
		if (confirmReverse) {
			return <div>{PAYABLE_DETAILS_REVERSE_CONFIRMATION_MSG()}</div>;
		}
		return renderPayableDetails();
	};

	return (
		<>
			<TableRow onClick={open} hover>
				<TableCell scope='row'>
					{formatUNIXTimestampWithTime(
						ledger.changeTimestamp,
						authData.auth.timezone,
					)}
				</TableCell>
				<TableCell>{formatLedgerType(ledger.changeType)}</TableCell>
				<TableCell align='right'>
					{formatPayableAmount(
						ledger.amountChange,
						currency,
						ledger.changeType === 'payable.reversed',
					)}
				</TableCell>
				<TableCell align='right'>
					{formatCurrency(Math.abs(ledger.reserveBalance), currency)}
				</TableCell>
			</TableRow>
			<DefaultDialog
				actions={confirmReverse ? confirmActions : detailActions}
				open={isOpen}
				onClose={close}
				title={heading}
			>
				{renderModalContent()}
			</DefaultDialog>
		</>
	);
};

export default withClaimContext<Props>(PayableDetailsModal);
